import React from 'react';
import { Link } from 'react-router-dom';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="container">
          <h1>About</h1>
          <p className="subtitle">Elite Training. Proven Results.</p>
        </div>
      </section>

      <section className="about-content">
        <div className="container">
          {/* SECTION 1: ABOUT XCELERATE FOOTBALL */}
          <div className="section-divider">
            <h2 className="section-title">About Xcelerate Football</h2>
          </div>

          <div className="about-grid">
            <div className="about-text">
              <h3>My Coaching Philosophy</h3>
              <p>
                At Xcelerate, we believe that every athlete has untapped potential waiting to be unleashed. 
                Through personalized 1-on-1 training and intensive film study, I help players develop not just 
                their physical skills, but their football IQ, mental toughness, and competitive edge.
              </p>
              <p>
                My approach combines technical fundamentals with advanced strategy, ensuring players understand 
                the "why" behind every move. Whether you're an offensive lineman perfecting your footwork or a 
                running back reading defensive schemes, we'll break down the game to elevate your performance.
              </p>
            </div>

            <div className="credentials-box-inline">
              <h3>Trained By Elite Coaches</h3>
              <div className="coaches-section">
                <h4>Head Coaches / Legends</h4>
                <ul className="credentials-list">
                  <li>✓ Bill Belichick (Patriots Legend, 6x SB Champ)</li>
                  <li>✓ Tom Osborne (Nebraska Icon, 3x National Champ)</li>
                  <li>✓ Matt Rhule (Nebraska HC)</li>
                  <li>✓ Brent Venables (Oklahoma HC)</li>
                  <li>✓ Chris Klieman (K-State HC)</li>
                  <li>✓ PJ Fleck (Minnesota HC)</li>
                </ul>
                <h4>Offensive Coordinators / Position Coaches</h4>
                <ul className="credentials-list">
                  <li>✓ Dana Holgorsen (Nebraska OC)</li>
                  <li>✓ Greg Harbaugh Jr. (Minnesota OC/QBs)</li>
                  <li>✓ Bill Bedenbaugh (Oklahoma OL Coach)</li>
                  <li>✓ Cooper Bassett (Oklahoma State OL Coach)</li>
                  <li>✓ Marcus Satterfield (Nebraska TE Coach)</li>
                  <li>✓ Joe Jon Finley (Oklahoma TE Coach)</li>
                  <li>✓ EJ Barthel (Nebraska RB Coach)</li>
                </ul>
                <h4>Defensive / Specialist</h4>
                <ul className="credentials-list">
                  <li>✓ Van Malone (K-State Asst HC/CBs)</li>
                  <li>✓ Rob Dvoracek (Nebraska LB Coach)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="approach-section">
            <h3>🔥 My Proven Method</h3>
            <div className="approach-grid">
              <div className="approach-card">
                <h4>✅ Custom Progress Tracking</h4>
                <p>Baseline IX score & skill test + measured gains all off-season</p>
              </div>
              <div className="approach-card">
                <h4>✅ 100% Personalized Plans</h4>
                <p>Built from YOUR film, body type & goals</p>
              </div>
              <div className="approach-card">
                <h4>✅ Elite Film Study</h4>
                <p>Your HS clips + College/Pro examples to master reads & technique</p>
              </div>
              <div className="approach-card">
                <h4>✅ Position-Specific Drills</h4>
                <p>Footwork, hand placement, pad level, run/pass schemes</p>
              </div>
              <div className="approach-card">
                <h4>✅ Full Off-Season Roadmap</h4>
                <p>Weekly check-ins & milestones from NOW through summer camp</p>
              </div>
              <div className="approach-card">
                <h4>📈 Results</h4>
                <p>College-ready with real, trackable growth</p>
              </div>
            </div>
          </div>

          <div className="mission-section">
            <h3>Our Mission</h3>
            <p>
              To provide world-class football training to athletes in the Omaha Metro area, regardless of their 
              financial situation. We offer low-income scholarships because we believe talent and work ethic 
              should never be limited by circumstance.
            </p>
          </div>

          {/* SECTION 2: ABOUT COACH MESA */}
          <div className="section-divider">
            <h2 className="section-title">About Coach Mesa</h2>
          </div>

          <div className="coach-story-section">
            <div className="story-text">
              <p>
                I grew up in Kansas City and the surrounding areas. Football wasn't part of my family's story, but it became mine. 
                At seventeen I joined the United States Air Force, served twenty years with eight deployments, and retired as a MSgt in 2021. 
                While serving, I always found a way to stay connected to the game: semi-pro ball, pickup leagues, and coaching whenever and wherever I could.
              </p>
              <p>
                I began as a volunteer high school coach in California, spent years developing middle school kids at Mission Middle School in Bellevue, 
                and now serve as Special Teams Coordinator and Offensive Line Coach at Bellevue East High School.
              </p>
              <p>
                I've been fortunate to learn directly from some of the best in the business:
              </p>
              <p className="coaches-inline">
                <strong>Matt Rhule • Bill Belichick • Brent Venables • Chris Klieman • PJ Fleck • Tom Osborne</strong>, 
                and coaches at Iowa State, Oklahoma State, Missouri, Kansas, Nebraska, and Minnesota.
              </p>
              <p className="philosophy-highlight">
                For me, coaching is about one thing: <strong>football teaches life.</strong>
              </p>
              <p>
                Ownership, leadership, toughness, accountability: these are the tools young men need to become better sons, brothers, husbands, fathers, and citizens.
              </p>
              <p className="signature">
                That's what I'm here to build.
              </p>
              <p className="signature-name">
                <strong>Coach Joe Mesa</strong><br />
                Xcelerate Football ⚡
              </p>
            </div>
          </div>

          <div className="journey-section">
            <h3>The Journey: Service to Coaching</h3>
            <div className="journey-grid">
              <div className="journey-photos military">
                <h4>Twenty years taught me hard lessons in leadership. Now I pass them on so young men don't have to learn the hard way.</h4>
                <div className="military-photos">
                  <img src="/images/coach-military1.jpg" alt="Coach Mesa serving in the Air Force" />
                  <img src="/images/coach-military2.jpg" alt="Coach Mesa on deployment" />
                </div>
              </div>
              <div className="journey-photos coaching">
                <h4>Special Teams Coordinator & OL Coach</h4>
                <div className="coaching-photos">
                  <img src="/images/coach-training.jpg" alt="Coach Mesa training athletes" />
                  <div className="coaching-photos-small">
                    <img src="/images/coach-kstate1.jpg" alt="Coach Mesa at K-State" />
                    <img src="/images/coach-kstate2.jpg" alt="Coach Mesa at K-State facility" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="cta-box">
            <h3>Ready to Start Your Journey?</h3>
            <p>Limited spots available. Book your training session today.</p>
            <Link to="/contact" className="btn btn-primary">Get Started</Link>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 Xcelerate Private Coaching. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;

